/// <reference lib="dom" />
import { ref, readonly, onUnmounted } from 'vue'
import { isSameOriginUrl } from '@/utils/security'

/**
 * Priority levels for navbar actions determine visibility across breakpoints
 * - primary: Always visible (critical actions like Export, Calculate)
 * - secondary: Hidden on mobile < 640px, visible on tablet+
 * - overflow: Always in kebab menu (Share, History, etc.)
 */
export type NavbarActionPriority = 'primary' | 'secondary' | 'overflow'

/**
 * Navbar action interface with priority-based responsive behavior
 */
export interface NavbarAction {
    id: string
    label: string
    icon?: string
    variant?: 'primary' | 'secondary' | 'text'
    loading?: boolean
    progress?: number
    disabled?: boolean
    onClick: () => void | Promise<void>
    priority: NavbarActionPriority
}

/**
 * Tag variants for different badge types (IS codes, versions, etc.)
 */
export type NavbarTagVariant = 'code' | 'version' | 'status' | 'info'

/**
 * Badge/chip displayed next to calculator title (e.g., "IS 456:2000", "v4.2.1")
 */
export interface NavbarTag {
    label: string
    variant?: NavbarTagVariant
    icon?: string
    color?: string
}

/**
 * Status indicator types for auto-save, calculation state, etc.
 */
export type NavbarStatusType = 'saved' | 'saving' | 'error' | 'info'

/**
 * Status badge displayed in navbar (e.g., "Auto-saved", "Calculating...")
 */
export interface NavbarStatus {
    type: NavbarStatusType
    message: string
}

/**
 * Save state for cloud save functionality
 */
export type SaveStateType = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error'

export interface NavbarSaveState {
    type: SaveStateType
    calculationId?: string
    onSave?: () => void | Promise<void>
}

/**
 * Complete navbar context for calculator modules
 * Registered by calculators via registerContext() in onMounted
 */
export interface NavbarContext {
    title?: string
    subtitle?: string
    tags?: NavbarTag[]
    status?: NavbarStatus
    saveState?: NavbarSaveState
    actions: NavbarAction[]
    showBackButton?: boolean
}

// Global reactive state (shared across all components)
const navbarState = ref<NavbarContext>({
    actions: []
})

// Export state for PDF generation
const exportProgress = ref(0)
const isExporting = ref(false)

/**
 * Composable for managing navbar context between calculators and AppHeader
 *
 * Usage in Calculator:
 * ```typescript
 * const { registerContext, updateStatus } = useNavbarContext()
 *
 * onMounted(() => {
 *   registerContext({
 *     title: 'Slab Design',
 *     tags: [{ label: 'IS 456:2000', variant: 'code' }],
 *     actions: [{
 *       id: 'export',
 *       label: 'Export',
 *       priority: 'primary',
 *       onClick: handleExport
 *     }]
 *   })
 * })
 * ```
 *
 * Usage in AppHeader:
 * ```typescript
 * const { context } = useNavbarContext()
 * // context is readonly reactive reference
 * ```
 */
export function useNavbarContext() {
    const requestAuthRequired = (message: string) => {
        window.dispatchEvent(new CustomEvent('auth:required', {
            detail: {
                message,
                returnUrl: `${window.location.pathname}${window.location.search}`,
                preservedInputs: true,
            }
        }))
    }

    /**
     * Register calculator-specific context (called from calculator pages)
     * Auto-clears on component unmount
     */
    const registerContext = (context: Partial<NavbarContext>) => {
        navbarState.value = {
            ...navbarState.value,
            ...context,
            actions: context.actions || []
        }
    }

    /**
     * Clear context (auto-called on unmount)
     */
    const clearContext = () => {
        navbarState.value = {
            actions: []
        }
    }

    /**
     * Update only status (for real-time feedback during calculations)
     */
    const updateStatus = (status: NavbarStatus | undefined) => {
        navbarState.value.status = status
    }

    /**
     * Update single action state (e.g., disable Export during PDF generation)
     */
    const updateAction = (actionId: string, updates: Partial<NavbarAction>) => {
        const index = navbarState.value.actions.findIndex(a => a.id === actionId)
        if (index === -1) return

        // Replace object to ensure reactivity propagates through readonly proxies
        navbarState.value.actions[index] = {
            ...navbarState.value.actions[index],
            ...updates
        }
    }

    /**
     * Enable an action by ID
     */
    const enableAction = (actionId: string) => {
        updateAction(actionId, { disabled: false })
    }

    /**
     * Disable an action by ID
     */
    const disableAction = (actionId: string) => {
        updateAction(actionId, { disabled: true })
    }

    /**
     * Update save state (for Save to Project functionality)
     */
    const updateSaveState = (saveState: NavbarSaveState | undefined) => {
        navbarState.value.saveState = saveState
    }

    /**
     * Set calculation as unsaved (after successful calculate)
     */
    const setUnsaved = (calculationId: string, onSave: () => void | Promise<void>) => {
        navbarState.value.saveState = {
            type: 'unsaved',
            calculationId,
            onSave
        }
    }

    /**
     * Set calculation as saved
     */
    const setSaved = () => {
        if (navbarState.value.saveState) {
            navbarState.value.saveState = {
                ...navbarState.value.saveState,
                type: 'saved'
            }
            // Reset to idle after 2 seconds
            setTimeout(() => {
                if (navbarState.value.saveState?.type === 'saved') {
                    navbarState.value.saveState = undefined
                }
            }, 2000)
        }
    }

    // Auto-cleanup on component unmount
    onUnmounted(() => {
        clearContext()
    })

    /**
     * Start PDF export with progress tracking (SSE)
     */
    const startExport = async (calculationId: string) => {
        if (isExporting.value) return

        // Pre-flight: silently renew the session before opening the SSE stream.
        // fetch() bypasses the Axios interceptor, so a stale access_token cookie
        // would cause a hard 401 mid-stream. If refresh returns 401 (both tokens
        // expired) we surface the auth modal immediately instead of starting a
        // broken stream.
        try {
            const refreshResp = await fetch('/api/v1/auth/refresh', {
                method: 'POST',
                credentials: 'include',
            })
            if (!refreshResp.ok && refreshResp.status === 401) {
                // Both tokens expired — clear stale auth state so the login page
                // doesn't show a "still logged in" navbar after the redirect.
                window.dispatchEvent(new CustomEvent('auth:session-invalidated'))
                requestAuthRequired('Your session has expired. Sign in again to export the PDF.')
                return
            }
        } catch {
            // Network error — proceed; the SSE 401 handler below will surface it
        }

        isExporting.value = true
        exportProgress.value = 0
        // Atomic update to prevent UI flicker/inconsistent state
        updateAction('export', { disabled: true, loading: true, progress: 0 })

        try {
            // Use fetch with credentials for authenticated SSE streaming
            const response = await fetch(`/api/v1/reports/generate-stream/${calculationId}`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'text/event-stream',
                }
            })

            if (!response.ok) {
                if (response.status === 401) {
                    // Clear stale auth state so the login page shows the correct
                    // logged-out navbar after the redirect.
                    window.dispatchEvent(new CustomEvent('auth:session-invalidated'))
                    requestAuthRequired('Your session expired while preparing the PDF. Sign in again to continue the export.')
                    return
                }
                if (response.status === 403) {
                    throw new Error('Access denied for this PDF export.')
                }

                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const reader = response.body?.getReader()
            if (!reader) {
                throw new Error('No response body')
            }

            const decoder = new TextDecoder()
            let buffer = ''

            let done = false
            while (!done) {
                const { done: streamDone, value } = await reader.read()
                if (streamDone) {
                    done = true
                    break
                }

                buffer += decoder.decode(value, { stream: true })
                const lines = buffer.split('\n')
                buffer = lines.pop() || ''

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6))

                            if (data.progress !== undefined) {
                                exportProgress.value = data.progress
                                updateAction('export', { progress: data.progress })
                            }

                            if (data.complete && data.downloadUrl) {
                                if (!isSameOriginUrl(data.downloadUrl)) {
                                    throw new Error('Download URL origin mismatch')
                                }
                                window.location.href = data.downloadUrl

                                // Reset state after delay
                                setTimeout(() => {
                                    isExporting.value = false
                                    exportProgress.value = 0
                                    enableAction('export')
                                    updateAction('export', { loading: false, progress: 0 })
                                }, 2000)
                                return
                            }

                            if (data.error) {
                                console.error('Export error:', data.error)
                                throw new Error(data.error)
                            }
                        } catch (e) {
                            if (e instanceof SyntaxError) {
                                console.warn('Failed to parse SSE data:', line)
                            } else {
                                throw e
                            }
                        }
                    }
                }
            }
        } catch (err) {
            console.error('SSE Export Error:', err)
            const errorMessage = err instanceof Error ? err.message : 'Unknown error'
            alert(`PDF export failed: ${errorMessage}`)
        } finally {
            isExporting.value = false
            enableAction('export')
            updateAction('export', { loading: false, progress: 0 })
        }
    }

    return {
        context: readonly(navbarState), // Read-only for navbar
        exportProgress: readonly(exportProgress),
        isExporting: readonly(isExporting),
        registerContext,
        clearContext,
        updateStatus,
        updateAction,
        enableAction,
        disableAction,
        updateSaveState,
        setUnsaved,
        setSaved,
        startExport
    }
}
